import { apiClient } from './axios';

import type {
  ContactCleanupMode,
  ContactCleanupStatus,
  ContactDuplicateGroup,
  ContactQuery,
  ContactTimelineResponse,
  ContactWithCommunications,
  ContactWithCounts,
  MergeContactsDto,
  MergeContactsResult,
  UpdateContactDto,
} from '@repo/shared-types';

export interface ToolWatchAlert {
  id: string;
  contact_id: string;
  tool_name: string;
  arguments_summary: string | null;
  created_at: string;
}

export interface ActiveSession {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  /** Call transport of the live session (PSTN, WEBRTC, WHATSAPP, WEB_WIDGET, EMAIL). */
  channel: string;
}

export interface PaginatedContacts {
  data: ContactWithCounts[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const contactsApi = {
  getAll: async (query: Partial<ContactQuery> = {}) => {
    const response = await apiClient.get<PaginatedContacts>('/contacts', { params: query });
    return response.data;
  },

  getOne: async (id: string) => {
    // 404s are handled by the calling UI (not-found states / grid self-heal),
    // so don't fire the global error toast for stale contact references.
    const response = await apiClient.get<ContactWithCommunications>(`/contacts/${id}`, {
      skipErrorToast: true,
    });
    return response.data;
  },

  getTimeline: async (
    id: string,
    params: { cursor?: string; limit?: number; include_tool_calls?: boolean } = {},
  ) => {
    // Only send the flag when true — z.coerce.boolean would read 'false' as true.
    const { include_tool_calls, ...rest } = params;
    const response = await apiClient.get<ContactTimelineResponse>(`/contacts/${id}/timeline`, {
      params: { ...rest, ...(include_tool_calls ? { include_tool_calls: true } : {}) },
    });
    return response.data;
  },

  getTags: async () => {
    const response = await apiClient.get<string[]>('/contacts/tags');
    return response.data;
  },

  update: async (id: string, data: UpdateContactDto) => {
    const response = await apiClient.patch<ContactWithCommunications>(`/contacts/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    await apiClient.delete(`/contacts/${id}`);
  },

  getDuplicates: async () => {
    const response = await apiClient.get<ContactDuplicateGroup[]>('/contacts/duplicates');
    return response.data;
  },

  merge: async (data: MergeContactsDto) => {
    // The backend merge transaction is allowed up to 60s; the global 10s axios
    // timeout was aborting slow merges client-side while they still committed,
    // and the retry then 404ed on already-deleted duplicates.
    const response = await apiClient.post<MergeContactsResult>('/contacts/merge', data, {
      timeout: 65000,
    });
    return response.data;
  },

  startCleanup: async (mode: ContactCleanupMode) => {
    const response = await apiClient.post<ContactCleanupStatus>('/contacts/deduplicate', { mode });
    return response.data;
  },

  getCleanupStatus: async () => {
    const response = await apiClient.get<ContactCleanupStatus>('/contacts/deduplicate/status');
    return response.data;
  },

  getActiveSessions: async () => {
    const response = await apiClient.get<ActiveSession[]>('/contacts/active-sessions');
    return response.data;
  },

  getToolWatchAlerts: async () => {
    const response = await apiClient.get<ToolWatchAlert[]>('/contacts/tool-watch-alerts');
    return response.data;
  },

  acknowledgeContactToolWatch: async (contactId: string) => {
    const response = await apiClient.post<{ acknowledged: number }>(
      `/contacts/${contactId}/acknowledge-tool-watch`,
    );
    return response.data;
  },
};
